// @flow
/* eslint-disable jsx-a11y/label-has-for,function-paren-newline */
import * as React from 'react';
import styled from 'styled-components';
import { colors } from 'bench-style-constants';

import { Form } from 'forms-core-components';
import { DatePeriod, CheckboxInput, ComboBoxInput } from 'primitive-components';
import { isRangeQuarter, isRangeYear } from 'date-functions';

import type { Period, SegmentCategory } from './types';

const StyledDatePeriod = styled(DatePeriod)`
  padding-right: 16px;
`;

const StyledCheckboxField = styled.div`
  display: grid;
  grid-template: auto auto / auto auto;
  column-gap: 8px;
  align-items: center;
`;

const StyledComboBoxField = styled.div`
  display: grid;
  grid-template: auto auto / 1fr 190px;
  column-gap: 16px;
  align-items: center;
`;

const StyledWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;

  ${StyledDatePeriod},
  ${StyledCheckboxField},
  ${StyledComboBoxField} {
    margin-bottom: 24px;
  }
`;

const StyledMessage = styled.div`
  background-color: ${colors.pearl};
  padding: 24px 32px;
  margin: 0 -32px -24px;
`;

type BreakdownPeriodOption = {
  value: Period,
  label: string,
};
export function getBreakdownPeriodOptions(
  startDate: Date,
  endDate: Date,
): Array<BreakdownPeriodOption> {
  const yearOption = { label: 'Year', value: 'yearly' };
  const quarterOption = { label: 'Quarter', value: 'quarterly' };
  const monthOption = { label: 'Month', value: 'monthly' };

  if (isRangeYear(startDate, endDate)) {
    return [yearOption, quarterOption, monthOption];
  }

  if (isRangeQuarter(startDate, endDate)) {
    return [quarterOption, monthOption];
  }

  return [monthOption];
}

type SegmentCategoryOption = {
  value: SegmentCategory,
  label: string,
};
function getCategoryOptions(
  segmentCategories: Array<SegmentCategory>,
): Array<SegmentCategoryOption> {
  if (!segmentCategories) {
    return [];
  }

  return segmentCategories.map(value => ({ value, label: value.name }));
}

function getSelectedCategoryOption(
  segmentCategory: SegmentCategory,
  segmentCategoryOptions: Array<SegmentCategoryOption>,
): ?SegmentCategoryOption {
  if (!segmentCategory) {
    return undefined;
  }

  const selectedOption = segmentCategoryOptions.find(
    option => option.value.id === segmentCategory.id,
  );

  return selectedOption && selectedOption;
}

type ReportParams = {
  breakdownPeriod: ?Period,
  segmentCategory: ?SegmentCategory,
};

type Props = React.ElementConfig<typeof Form> & {
  startDate: Date,
  endDate: Date,
  onSubmit: ReportParams => void,
  segmentCategories?: Array<SegmentCategory>,
  segmentCategory?: SegmentCategory,
};

type State = {
  breakdownPeriod: BreakdownPeriodOption,
  segmentCategory: ?SegmentCategoryOption,
  withSegment: boolean,
};

class ReportsDownloadForm extends React.Component<Props, State> {
  static defaultProps = {
    segmentCategories: [],
    segmentCategory: null,
  };

  constructor(props: Props) {
    super(props);
    const { endDate, segmentCategory, segmentCategories, startDate } = this.props;
    this.breakdownPeriodOptions = getBreakdownPeriodOptions(startDate, endDate);
    this.showSegmentOptions = !!segmentCategory;
    this.segmentCategoryOptions = getCategoryOptions(segmentCategories);

    this.state = {
      breakdownPeriod: this.breakdownPeriodOptions[0],
      segmentCategory: getSelectedCategoryOption(segmentCategory, this.segmentCategoryOptions),
      withSegment: !!this.props.segmentCategory,
    };
  }

  breakdownPeriodOptions: Array<BreakdownPeriodOption>;
  showSegmentOptions: boolean;
  segmentCategoryOptions: Array<SegmentCategoryOption>;

  handleSubmit = (ev: SyntheticEvent<HTMLFormElement>): void => {
    ev.preventDefault();
    const segmentCategory =
      this.state.withSegment && this.state.segmentCategory
        ? this.state.segmentCategory.value
        : null;
    const breakdownPeriod = !this.state.withSegment ? this.state.breakdownPeriod.value : null;
    this.props.onSubmit({ breakdownPeriod, segmentCategory });
  };

  render() {
    const { endDate, id, startDate } = this.props;
    return (
      <form id={id} onSubmit={this.handleSubmit}>
        <StyledWrapper>
          <StyledDatePeriod size="large" startDate={startDate} endDate={endDate} />
          {this.showSegmentOptions && (
            <StyledCheckboxField>
              <CheckboxInput
                id="withSegment"
                onChange={checked => this.setState({ withSegment: checked })}
                startChecked
              />
              <label htmlFor="withSegment">With tag information</label>
            </StyledCheckboxField>
          )}
          {this.state.withSegment && (
            <StyledComboBoxField>
              <label htmlFor="segmentCategory">Group:</label>
              <ComboBoxInput
                clearable={false}
                id="segmentCategory"
                onChange={option => {
                  if (this.state.segmentCategory === option) return;
                  this.setState({ segmentCategory: option });
                }}
                options={this.segmentCategoryOptions}
                value={this.state.segmentCategory}
              />
            </StyledComboBoxField>
          )}
          {!this.state.withSegment && this.breakdownPeriodOptions.length > 1 && (
            <StyledComboBoxField>
              <label htmlFor="breakdownPeriod">Broken down by:</label>
              <ComboBoxInput
                clearable={false}
                id="breakdownPeriod"
                onChange={option => {
                  if (this.state.breakdownPeriod === option) return;
                  this.setState({ breakdownPeriod: option });
                }}
                options={this.breakdownPeriodOptions}
                value={this.state.breakdownPeriod}
              />
            </StyledComboBoxField>
          )}
        </StyledWrapper>
        <StyledMessage>
          {this.state.withSegment ? (
            <span>
              This financial statement contains information not created or reviewed by your
              bookkeeping team and should only be used for informational purposes.
            </span>
          ) : (
            <span>
              This data is not intended to be used for tax filing, please visit the Tax section in
              Documents to download your year-end financial package.
            </span>
          )}
        </StyledMessage>
      </form>
    );
  }
}

export default ReportsDownloadForm;
